// Renders the first page of a PDF into a small JPEG data: URL suitable for use as a
// thumbnail. pdfjs (and its web worker) are pulled in via a dynamic import of
// ./pdfjsClient so they land in a lazy bundle chunk and never load unless a PDF is
// actually previewed. Keeping the import inside the function also means the Vite-only
// `?url` worker suffix (in pdfjsClient) is never evaluated by jest/vitest when they
// load the AssetPreview component tree.

const DEFAULT_TARGET_WIDTH = 400;

// Never render more than a few PDFs at once. A non-windowed grid can mount dozens of
// PdfPreviews simultaneously; without a cap every one spins up a worker and races
// render() on the main thread. Extra requests queue until a slot frees up.
const MAX_CONCURRENT_RENDERS = 3;

// Cap the cache so a long-lived session (lots of grid scrolling) can't grow the Map
// without bound. Least-recently-used entries are evicted first.
const MAX_CACHE_ENTRIES = 24;

// Cache in-flight / resolved renders so re-mounts (grid scroll, modal re-open) reuse
// the same thumbnail instead of re-fetching and re-rendering the PDF. Keyed on a
// caller-supplied stable key (assetId + updatedAt) rather than the raw presigned
// sourceUrl, so a replaced PDF doesn't keep serving its old thumbnail.
const cache = new Map();

let activeRenders = 0;
const queue = [];

function pumpQueue() {
  while (activeRenders < MAX_CONCURRENT_RENDERS && queue.length) {
    const run = queue.shift();
    run();
  }
}

// Resolves once a concurrency slot is free; the returned function must be called to
// release the slot.
function acquireSlot() {
  return new Promise((resolve) => {
    queue.push(() => {
      activeRenders += 1;
      resolve(() => {
        activeRenders -= 1;
        pumpQueue();
      });
    });
    pumpQueue();
  });
}

function touchCache(key) {
  // Re-insert to mark as most-recently-used (Map preserves insertion order).
  const value = cache.get(key);
  cache.delete(key);
  cache.set(key, value);
  return value;
}

function setCache(key, value) {
  cache.set(key, value);
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    cache.delete(oldestKey);
  }
}

async function loadPdfjs() {
  const mod = await import('./pdfjsClient');
  return mod.default;
}

async function renderToDataUrl(sourceUrl, targetWidth) {
  const pdfjs = await loadPdfjs();

  // Only the first page is ever needed, so stop pdfjs from background-fetching the
  // rest of the file (relies on the origin supporting range requests).
  const pdf = await pdfjs.getDocument({
    url: sourceUrl,
    disableAutoFetch: true,
    disableStream: true
  }).promise;

  try {
    const page = await pdf.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = targetWidth / baseViewport.width;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));

    const context = canvas.getContext('2d');
    // PDF pages can be transparent; paint white so the thumbnail isn't see-through.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;

    // Use a data: URL (not blob:) — the app CSP allows `img-src data:` but not `blob:`.
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    page.cleanup();
    return dataUrl;
  } finally {
    pdf.destroy();
  }
}

async function renderWithConcurrencyLimit(sourceUrl, targetWidth) {
  const release = await acquireSlot();
  try {
    return await renderToDataUrl(sourceUrl, targetWidth);
  } finally {
    release();
  }
}

export default function renderPdfThumbnail(
  sourceUrl,
  { targetWidth = DEFAULT_TARGET_WIDTH, cacheKey } = {}
) {
  if (!sourceUrl) {
    return Promise.reject(new Error('renderPdfThumbnail: missing sourceUrl'));
  }

  const key = cacheKey || sourceUrl;

  if (cache.has(key)) {
    return touchCache(key);
  }

  const promise = renderWithConcurrencyLimit(sourceUrl, targetWidth).catch((error) => {
    // Don't cache failures — allow a later retry (e.g. transient network / CORS).
    cache.delete(key);
    throw error;
  });

  setCache(key, promise);
  return promise;
}
